import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Volume2 } from 'lucide-react';
import { getBaseUrl, apiClient } from '@/services/httpClient';
import { useAuth } from '@/contexts/AuthContext';
import ContentBadge from '@/components/ContentBadge';
import { getSosCalmSounds } from '@/services/sosAudioService';
import { audioSessionManager } from '@/services/audioSessionManager';
import { registerPlugin } from '@capacitor/core';

const NativeAudio = registerPlugin('NativeAudio');

const SOSCalm = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSafeMessage, setShowSafeMessage] = useState(true);
  const safeMessageTimerRef = useRef(null);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [voiceVolume, setVoiceVolume] = useState(0.25);
  const [musicVolume, setMusicVolume] = useState(0.80);
  const [messages, setMessages] = useState([]);
  const [welcomeMessages, setWelcomeMessages] = useState([]);
  const [calmInterval, setCalmInterval] = useState(60);
  const [musicTracks, setMusicTracks] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(-1);
  const [voiceMessageCount, setVoiceMessageCount] = useState(0);
  const [showPremiumBanner, setShowPremiumBanner] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [premiumCounterMessage, setPremiumCounterMessage] = useState('');
  const [shuffledMessages, setShuffledMessages] = useState([]);
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentVoiceText, setCurrentVoiceText] = useState(''); // Track displayed voice message text
  const [voiceTextVisible, setVoiceTextVisible] = useState(false); // Controls fade in/out

  /**
   * Strip SSML tags from voice message text for display
   */
  const cleanTextForDisplay = (text) => {
    if (!text) return '';
    return text.replace(/<[^>]+>/g, ' ').replace(/\s{2,}/g, ' ').trim();
  };
  const currentAudioRef = useRef(null);
  const messageIntervalRef = useRef(null);
  const lastMusicTrackRef = useRef(null); // Track last played music to avoid repetition
  const musicQueueRef = useRef([]); // Shuffled queue of music tracks (no repeat until all played)
  const voiceMessageCountRef = useRef(0); // Ref to track voice message count in real-time
  const [playedMessageIds, setPlayedMessageIds] = useState([]); // Track played message IDs for UI
  const playedMessageIdsRef = useRef([]); // Ref for real-time tracking of played messages
  const duckIntervalRef = useRef(null); // Track ducking animation
  const musicVolumeRef = useRef(0.30);
  const musicInitialVolumeRef = useRef(0.30);
  const startRegularCycleRef = useRef(null); // Callback to start regular messages after welcome ends
  const freeSessionTimerRef = useRef(null); // V2 GATING: 5-minute session limit for free users
  const FREE_SESSION_DURATION = 5 * 60 * 1000; // 5 minutes in ms

  // Web Audio API refs for iOS volume control
  const audioCtxRef = useRef(null);
  const voiceGainRef = useRef(null);
  const musicGainRef = useRef(null);
  const [nextTrackCooldown, setNextTrackCooldown] = useState(false);
  const [endSessionCooldown, setEndSessionCooldown] = useState(false);

  // Preload refs for instant startup
  const preloadedMusicRef = useRef(null);       // Pre-loaded Audio element (first track)
  const preloadedWelcomeRef = useRef(null);     // Pre-decoded AudioBuffer (welcome voice)
  const preloadedWelcomeMsgRef = useRef(null);  // Welcome message object (for metadata)

  /**
 * Initialize AudioContext + GainNodes (must be called in user gesture)
 */
const ensureAudioContext = () => {
  if (audioCtxRef.current) return audioCtxRef.current;
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  audioCtxRef.current = ctx;
  voiceGainRef.current = ctx.createGain();
  voiceGainRef.current.gain.value = voiceVolume * 4;
  voiceGainRef.current.connect(ctx.destination);
  musicGainRef.current = ctx.createGain();
  musicGainRef.current.gain.value = musicVolume;
  musicGainRef.current.connect(ctx.destination);
  console.log('[SOS Calm] AudioContext + GainNodes created');
  return ctx;
};

/**
 * Lockscreen Play/Pause commands from iOS
 */
useEffect(() => {
  let listener;

  const setupRemoteCommands = async () => {
    try {
      listener = await NativeAudio.addListener(
        'remoteCommand',
        ({ command }) => {
          console.log(
            '[SOS Calm][Lockscreen] remoteCommand:',
            command
          );

          const bgMusic =
            currentAudioRef.current?.backgroundMusic;

          if (command === 'pause') {
            if (bgMusic && !bgMusic.paused) {
              bgMusic.pause();
            }

            setIsPlaying(false);

            audioSessionManager.setPlaying(
              'sos_calm',
              false,
              'lockscreen-pause'
            );
          }

          if (command === 'play') {
            if (bgMusic && bgMusic.paused) {
              bgMusic.play().catch((err) => {
                console.warn(
                  '[SOS Calm][Lockscreen] play failed:',
                  err
                );
              });
            }

            setIsPlaying(true);

            audioSessionManager.setPlaying(
              'sos_calm',
              true,
              'lockscreen-play'
            );
          }
        }
      );
    } catch (err) {
      console.warn(
        '[SOS Calm][Lockscreen] remote command listener unavailable:',
        err
      );
    }
  };

  setupRemoteCommands();

  return () => {
    listener?.remove?.();
  };
}, []);

useEffect(() => {
  if (!('mediaSession' in navigator)) return;

  navigator.mediaSession.metadata = new window.MediaMetadata({
    title: 'SOS Calm',
    artist: 'Hearen',
    album: 'Guided by Hera',
    artwork: [
      { src: '/assets/hearen-logo-final.png', sizes: '512x512', type: 'image/png' }
    ]
  });

  navigator.mediaSession.setActionHandler('pause', () => {
    console.log('[SOS Calm][MediaSession] pause');

    const bgMusic = currentAudioRef.current?.backgroundMusic;
    const voice = currentAudioRef.current?.voiceAudio;

    if (bgMusic && !bgMusic.paused) bgMusic.pause();
    if (voice && !voice.paused) voice.pause();

    setIsPlaying(false);
    audioSessionManager.setPlaying('sos_calm', false, 'mediasession-pause');
  });

  navigator.mediaSession.setActionHandler('play', () => {
    console.log('[SOS Calm][MediaSession] play');

    const bgMusic = currentAudioRef.current?.backgroundMusic;

    if (bgMusic && bgMusic.paused) {
      bgMusic.play().catch((err) => {
        console.warn('[SOS Calm][MediaSession] play failed:', err);
      });
    }

    setIsPlaying(true);
    audioSessionManager.setPlaying('sos_calm', true, 'mediasession-play');
  });

  return () => {
    navigator.mediaSession.setActionHandler('pause', null);
    navigator.mediaSession.setActionHandler('play', null);
  };
}, []);
  

  const locale = t?.locale === 'fr-FR' ? 'fr-FR' : 'en-US';
  const isFreeUser = !user?.isPremium && user?.subscription !== 'premium';
  const FREE_MESSAGE_LIMIT = 2; // FREE users can listen to 2 voice messages

  /**
   * Smoothly transition background music volume (ducking)
   * Uses audio.volume directly on the Audio element
   * @param {number} targetVolume - Target volume (0-1)
   * @param {number} durationMs - Transition duration in ms
   */
  const duckMusic = (targetVolume, durationMs = 400) => {
    if (duckIntervalRef.current) {
      clearInterval(duckIntervalRef.current);
      duckIntervalRef.current = null;
    }

    const bgMusic = currentAudioRef.current?.backgroundMusic;
    if (!bgMusic) return;

    const startVolume = bgMusic.volume;
    const diff = targetVolume - startVolume;
    if (Math.abs(diff) < 0.01) {
      bgMusic.volume = Math.max(0, Math.min(1, targetVolume));
      return;
    }
    const steps = Math.max(Math.round(durationMs / 25), 1);
    const stepSize = diff / steps;
    let step = 0;
    duckIntervalRef.current = setInterval(() => {
      step++;
      if (step >= steps) {
        bgMusic.volume = Math.max(0, Math.min(1, targetVolume));
        clearInterval(duckIntervalRef.current);
        duckIntervalRef.current = null;
      } else {
        bgMusic.volume = Math.max(0, Math.min(1, startVolume + stepSize * step));
      }
    }, 25);
  };
  
  /**
   * Fisher-Yates shuffle algorithm
   */
  const shuffleArray = (array) => {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };
  
  /**
   * Initialize or refresh the shuffled message playlist
   */
  const refreshMessagePlaylist = (messageList) => {
    if (messageList.length === 0) return [];
    const shuffled = shuffleArray(messageList);
    setShuffledMessages(shuffled);
    setMessageIndex(0);
    console.log(`[SOS Calm] Messages shuffled: ${shuffled.length} messages in new random order`);
    return shuffled;
  };

  useEffect(() => {
    loadCalmingMessages();
    loadMusicTracks();
    
    return () => {
      stopAudio();
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
      }
      // Cleanup preloaded audio
      if (preloadedMusicRef.current?.audio) {
        preloadedMusicRef.current.audio.src = '';
        preloadedMusicRef.current = null;
      }
      preloadedWelcomeRef.current = null;
      preloadedWelcomeMsgRef.current = null;
      // Close AudioContext
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
        voiceGainRef.current = null;
        musicGainRef.current = null;
      }
      // Cleanup audioSessionManager
      audioSessionManager.setPlaying('sos_calm', false, 'unmount');
      audioSessionManager.releaseSession('sos_calm', true);
      audioSessionManager.unregisterResumeCallback('sos_calm');
      audioSessionManager.unregisterStopCallback('sos_calm');
      if (safeMessageTimerRef.current) clearTimeout(safeMessageTimerRef.current);
      if (freeSessionTimerRef.current) clearTimeout(freeSessionTimerRef.current);
    };
  }, [user, locale]);

  const loadCalmingMessages = async () => {
    if (!user) {
      return;
    }
    
    try {
      const lang = locale === 'fr-FR' ? 'fr' : 'en';
      const voice = 'hera'; // Default voice
      
      // V1.5: Use apiClient for iOS Capacitor compatibility
      const response = await apiClient.get(
        `/api/radio/voice-messages/sos_calm?user_id=${user.id}&lang=${lang}&voice=${voice}`
      );
      const data = response.data;
      
      if (data.messages && data.messages.length > 0) {
        // Separate welcome messages from regular ones
        const welcome = data.messages.filter(m => m.message_role === 'welcome');
        const regular = data.messages.filter(m => m.message_role !== 'welcome');
        
        setWelcomeMessages(welcome);
        setMessages(regular);
        // Initialize shuffled playlist with regular messages only
        refreshMessagePlaylist(regular);
        console.log(`[SOS Calm] Loaded ${welcome.length} welcome + ${regular.length} regular messages`);

        // Preload first welcome voice for instant startup
        if (welcome.length > 0) {
          const msg = welcome[Math.floor(Math.random() * welcome.length)];
          preloadedWelcomeMsgRef.current = msg;
          try {
            const baseUrl = getBaseUrl();
            const res = await fetch(`${baseUrl}/api/radio/voice-audio/${msg.id}?stream=true`);
            if (res.ok) {
              const buf = await res.arrayBuffer();
              preloadedWelcomeRef.current = buf;
              console.log(`[SOS Calm] Preloaded welcome voice: ${msg.id}`);
            }
          } catch (e) {
            console.warn('[SOS Calm] Welcome preload failed (will fetch on start):', e.message);
          }
        }
      } else {
      }
      
      // Load calm interval setting
      try {
        const intervalResp = await apiClient.get('/api/admin/settings/calm-interval');
        if (intervalResp.data?.interval_seconds) {
          setCalmInterval(intervalResp.data.interval_seconds);
          console.log(`[SOS Calm] Interval: ${intervalResp.data.interval_seconds}s`);
        }
      } catch (e) { /* keep default 60s */ }
    } catch (error) {
      console.error('Error loading calming messages:', error);
    }
  };

  const loadMusicTracks = async () => {
    try {
      const sounds = await getSosCalmSounds();
      
      if (sounds && sounds.length > 0) {
        const tracks = sounds.map(sound => sound.url);
        setMusicTracks(tracks);

        // Preload first track for instant startup
        const shuffled = shuffleArray(tracks);
        musicQueueRef.current = shuffled;
        const firstUrl = shuffled[0];
        if (firstUrl) {
          const audio = new Audio();
          audio.crossOrigin = 'anonymous';
          audio.preload = 'auto';
          audio.src = firstUrl;
          audio.load();
          preloadedMusicRef.current = { audio, url: firstUrl };
          console.log(`[SOS Calm] Preloading first music track: ${firstUrl.split('/').pop()}`);
        }
      } else {
        setMusicTracks([]);
      }
    } catch (error) {
      console.error('❌ [SOS Calm] Error loading music tracks:', error);
      setMusicTracks([]);
    }
  };

  const playVoiceMessage = async (message) => {
    if (!isSoundEnabled || !message || !message.id) {
      return;
    }

    const ctx = audioCtxRef.current;
    if (!ctx) {
      console.error('[SOS Calm] No AudioContext available');
      return;
    }

    // Resume AudioContext if suspended (iOS Safari)
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch (e) { console.warn('[SOS Calm] ctx.resume failed:', e); }
    }

    try {
      const baseUrl = getBaseUrl();
      let audioUrl = `${baseUrl}/api/radio/voice-audio/${message.id}?stream=true`;

      console.log(`🎤 [SOS Calm] Fetching voice: ${message.id}`);

      // Show text only for regular messages
      if (message.message_role !== 'welcome') {
        setCurrentVoiceText(cleanTextForDisplay(message.text));
        setVoiceTextVisible(true);
      }

      // Fetch audio as blob (preloaded or network)
      let blobUrl;
      if (message.message_role === 'welcome' && preloadedWelcomeRef.current && preloadedWelcomeMsgRef.current?.id === message.id) {
        console.log(`[SOS Calm] Using preloaded welcome (instant)`);
        const blob = new Blob([preloadedWelcomeRef.current], { type: 'audio/mpeg' });
        blobUrl = URL.createObjectURL(blob);
        preloadedWelcomeRef.current = null;
      } else {
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);
      }

      // Stop previous voice audio if any
      if (currentAudioRef.current?.voiceAudio) {
        try {
          currentAudioRef.current.voiceAudio.onended = null;
          currentAudioRef.current.voiceAudio.pause();
          currentAudioRef.current.voiceAudio.src = '';
          URL.revokeObjectURL(currentAudioRef.current.voiceBlobUrl);
        } catch (e) { /* already stopped */ }
      }

      // Play voice via HTML <audio> element (survives iOS lock screen)
      const voiceAudio = new Audio();
      voiceAudio.src = blobUrl;
      const baseVoiceVolume = voiceGainRef.current?.gain?.value ?? 1.0;
      const heraBoost = locale === 'en-US' ? 1.35 : 1.0;

      voiceAudio.volume = Math.min(1, baseVoiceVolume * heraBoost);

      if (!currentAudioRef.current) currentAudioRef.current = {};
      currentAudioRef.current.voiceAudio = voiceAudio;
      currentAudioRef.current.voiceBlobUrl = blobUrl;

      // Duck BEFORE voice starts
      duckMusic(musicVolumeRef.current * 0.02, 250);

      setTimeout(() => {
        voiceAudio.play();
        console.log(`✅ [SOS Calm] Voice playing (HTML audio): ${message.id}`);
      }, 280);

      voiceAudio.onended = () => {
        URL.revokeObjectURL(blobUrl);
        // Restore background music volume after voice ends
        duckMusic(musicVolumeRef.current, 600);

        // Fade out displayed message text, then clear
        setVoiceTextVisible(false);
        setTimeout(() => setCurrentVoiceText(''), 1200);

        // Mark current message as played
        playedMessageIdsRef.current = [...playedMessageIdsRef.current, message.id];
        setPlayedMessageIds(playedMessageIdsRef.current);

        // If this was a welcome message, start the regular message cycle now
        if (message.message_role === 'welcome' && startRegularCycleRef.current) {
          startRegularCycleRef.current();
          startRegularCycleRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error playing voice message:', error);
      // Restore music if voice fails
      duckMusic(musicVolumeRef.current, 400);
      setVoiceTextVisible(false);
      setTimeout(() => setCurrentVoiceText(''), 500);

      // Still trigger welcome->regular transition on error
      if (message.message_role === 'welcome' && startRegularCycleRef.current) {
        startRegularCycleRef.current();
        startRegularCycleRef.current = null;
      }
    }
  };

  const playRandomBackgroundMusic = async () => {
    if (!currentAudioRef.current?.isPlayingSession) return;

    if (!musicTracks || musicTracks.length === 0) {
      console.error('❌ [SOS Calm] No music tracks available!');
      return;
    }

    const ctx = audioCtxRef.current;
    if (ctx && ctx.state === 'suspended') {
      try { await ctx.resume(); } catch (e) {}
    }

    // Check if we have a preloaded track ready
    const preloaded = preloadedMusicRef.current;
    let bgMusic;
    let randomTrack;

    if (preloaded && preloaded.audio) {
      // Use preloaded Audio element (instant)
      bgMusic = preloaded.audio;
      randomTrack = preloaded.url;
      preloadedMusicRef.current = null; // consume it
      lastMusicTrackRef.current = randomTrack;
      // Remove from queue if present
      musicQueueRef.current = musicQueueRef.current.filter(t => t !== randomTrack);
      console.log(`[SOS Calm] Using preloaded music (instant): ${randomTrack.split('/').pop()}`);
    } else {
      // Pick next track from shuffled queue (no repetition until all played)
      if (musicQueueRef.current.length === 0) {
        let shuffled = shuffleArray(musicTracks);
        if (shuffled.length > 1 && shuffled[0] === lastMusicTrackRef.current) {
          [shuffled[0], shuffled[shuffled.length - 1]] = [shuffled[shuffled.length - 1], shuffled[0]];
        }
        musicQueueRef.current = shuffled;
      }
      randomTrack = musicQueueRef.current.shift();
      lastMusicTrackRef.current = randomTrack;

      bgMusic = new Audio();
      bgMusic.crossOrigin = 'anonymous';
      bgMusic.preload = 'auto';
      console.log(`[SOS Calm] Loading music (network): ${randomTrack.split('/').pop()}`);
    }

    // Configure the Audio element (whether preloaded or new)
    bgMusic.loop = false;
    bgMusic.volume = musicInitialVolumeRef.current;
    // Reset initial volume to full for subsequent tracks
    musicInitialVolumeRef.current = musicVolumeRef.current;

    if (!currentAudioRef.current) currentAudioRef.current = {};
    currentAudioRef.current.backgroundMusic = bgMusic;

    bgMusic.onerror = () => {
      const err = bgMusic.error;
      console.error(`❌ [SOS Calm] Music error: code=${err?.code}, message=${err?.message}`);
      // Retry with next track
      if (currentAudioRef.current?.isPlayingSession) {
        setTimeout(() => playRandomBackgroundMusic(), 2000);
      }
    };

    bgMusic.onended = () => {
      if (currentAudioRef.current?.isPlayingSession) {
        setTimeout(() => playRandomBackgroundMusic(), 500);
      }
    };

    // Set src only for non-preloaded tracks (preloaded already has src+data)
    if (!preloaded) {
      bgMusic.src = randomTrack;
      bgMusic.load();
    }

    // Play when ready
    const playWhenReady = async () => {
      if (!currentAudioRef.current?.isPlayingSession) return;
      try {
        console.log('[SOS Calm][DEBUG before play]', {
          src: bgMusic.src,
          readyState: bgMusic.readyState,
          paused: bgMusic.paused,
          volume: bgMusic.volume,
          randomTrack,
        });

        
        await bgMusic.play();
        console.log(`✅ [SOS Calm] Music playing: ${randomTrack}`);
      } catch (err) {
        console.error(`❌ [SOS Calm] Music play failed: ${err.message}`);
        if (currentAudioRef.current?.isPlayingSession) {
          setTimeout(() => playRandomBackgroundMusic(), 2000);
        }
      }
    };

    if (bgMusic.readyState >= 2) {
      playWhenReady();
    } else {
      bgMusic.addEventListener('loadeddata', playWhenReady, { once: true });
      bgMusic.addEventListener('canplay', playWhenReady, { once: true });

      setTimeout(() => {
        if (currentAudioRef.current?.isPlayingSession && bgMusic.paused) {
          console.log('[SOS Calm] Forcing play after preload timeout', {
            readyState: bgMusic.readyState,
            src: bgMusic.src,
          });
          playWhenReady();
        }
      }, 1200);
    }
  };

  const startCalming = async () => {
    console.log('[SOS Calm] Starting session...');

    // Acquire audio session (stops other audio owners like radio/soundscape)
    await audioSessionManager.acquireSession('sos_calm');
    audioSessionManager.setStarting('sos_calm', true);

    // Initialize AudioContext for iOS volume control (must be in user gesture)
    const ctx = ensureAudioContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
      console.log('[SOS Calm] AudioContext resumed');
    }

    // Unlock audio for WKWebView/Capacitor
    await audioSessionManager.unlockAudioOnce('sos_calm');

    // Hide "Tu es en sécurité ici" after 60 seconds
    setShowSafeMessage(true);
    if (safeMessageTimerRef.current) clearTimeout(safeMessageTimerRef.current);
    safeMessageTimerRef.current = setTimeout(() => setShowSafeMessage(false), 60000);
    
    // ULTRA-SIMPLE VERSION for iPhone compatibility
    setIsPlaying(true);
    setShowPremiumBanner(false); // Hide banner if starting new session
    setIsLocked(false); // Unlock controls
    setVoiceMessageCount(0); // Reset voice message counter for FREE users
    voiceMessageCountRef.current = 0; // Reset ref as well
    
    // Set initial premium counter message for FREE users
    if (isFreeUser) {
      setPremiumCounterMessage(
        locale === 'fr-FR' 
          ? `✨ Session gratuite : 5 minutes`
          : `✨ Free session: 5 minutes`
      );
      // V2 GATING: Start 5-minute session timer
      if (freeSessionTimerRef.current) clearTimeout(freeSessionTimerRef.current);
      freeSessionTimerRef.current = setTimeout(() => {
        console.log('[SOS Calm] FREE session limit reached (5 min)');
        triggerFreeUserFadeout();
      }, FREE_SESSION_DURATION);
    } else {
      setPremiumCounterMessage('');
    }
    
    // Reset shuffled playlist for new session (use return value, don't rely on async state)
    let localShuffled = [];
    if (messages.length > 0) {
      localShuffled = refreshMessagePlaylist(messages);
    }
    
    if (!currentAudioRef.current) currentAudioRef.current = {};
    currentAudioRef.current.isPlayingSession = true;
    
    const hasRegularMessages = isSoundEnabled && localShuffled.length > 0;
    const hasWelcome = welcomeMessages.length > 0;
    
    console.log(`[SOS Calm] Start: ${welcomeMessages.length} welcome, ${messages.length} regular, interval=${calmInterval}s, sound=${isSoundEnabled}`);
    
    if (hasRegularMessages || hasWelcome) {
      // If welcome message will play, pre-duck music volume BEFORE music starts
      if (hasWelcome) {
        musicInitialVolumeRef.current = musicVolumeRef.current * 0.05;
      } else {
        musicInitialVolumeRef.current = musicVolumeRef.current;
      }
      
      // Play background music
      playRandomBackgroundMusic();
      
      // V1.4.0: Play welcome IMMEDIATELY (must stay in user gesture context for iOS)
      if (hasWelcome) {
        const randomWelcome = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
        console.log(`[SOS Calm] Playing welcome NOW: "${randomWelcome.text?.substring(0, 50)}..."`);
        playVoiceMessage(randomWelcome);
      }
      
      // Regular messages: start cycle AFTER welcome ends (or immediately if no welcome)
      if (hasRegularMessages) {
        let regularIdx = 0;
        const intervalMs = calmInterval * 1000;
        
        const beginRegularCycle = () => {
          if (!currentAudioRef.current?.isPlayingSession) return;
          console.log(`[SOS Calm] Starting regular cycle: first in ${calmInterval}s, then every ${calmInterval}s`);
          
          if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
          
          // Wait one full interval before the first regular message
          const initialTimer = setTimeout(() => {
            if (!currentAudioRef.current?.isPlayingSession) return;
            
            const msg = localShuffled[regularIdx % localShuffled.length];
            console.log(`[SOS Calm] Regular #${regularIdx + 1}: "${msg.text?.substring(0, 40)}..."`);
            playVoiceMessage(msg);
            regularIdx++;
            
            // Then repeat every interval
            messageIntervalRef.current = setInterval(() => {
              if (!currentAudioRef.current?.isPlayingSession) {
                clearInterval(messageIntervalRef.current);
                return;
              }
              const m = localShuffled[regularIdx % localShuffled.length];
              console.log(`[SOS Calm] Regular #${regularIdx + 1}: "${m.text?.substring(0, 40)}..."`);
              playVoiceMessage(m);
              regularIdx++;
              if (regularIdx % localShuffled.length === 0) {
                const reshuffled = [...localShuffled].sort(() => Math.random() - 0.5);
                localShuffled.splice(0, localShuffled.length, ...reshuffled);
              }
            }, intervalMs);
          }, intervalMs);
          
          if (!currentAudioRef.current) currentAudioRef.current = {};
          currentAudioRef.current.initialTimer = initialTimer;
        };
        
        if (hasWelcome) {
          // Defer: will be called by onended when welcome finishes
          startRegularCycleRef.current = beginRegularCycle;
        } else {
          // No welcome: start immediately
          beginRegularCycle();
        }
      }
    }

    // Register with audioSessionManager for background/foreground recovery
    audioSessionManager.setPlaying('sos_calm', true, 'session_start');
    audioSessionManager.setStarting('sos_calm', false);

    // Resume callback: called when app returns to foreground
    audioSessionManager.registerResumeCallback('sos_calm', async () => {
      console.log('[SOS Calm] Foreground resume triggered');
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state === 'suspended') {
        try { await ctx.resume(); } catch (e) {}
        console.log('[SOS Calm] AudioContext resumed after foreground');
      }
      // Resume music if it was playing
      const bgMusic = currentAudioRef.current?.backgroundMusic;
      if (bgMusic && bgMusic.paused && currentAudioRef.current?.isPlayingSession) {
        try {
          await bgMusic.play();
          console.log('[SOS Calm] Music resumed after foreground');
        } catch (e) {
          console.warn('[SOS Calm] Music resume failed, playing next track');
          playRandomBackgroundMusic();
        }
      }
    });

    // Stop callback: called when another service acquires the session
    audioSessionManager.registerStopCallback('sos_calm', () => {
      console.log('[SOS Calm] Stopped by audioSessionManager (another service acquired session)');
      stopAudio();
    });
  };

  const triggerFreeUserFadeout = () => {
    // Lock controls
    setIsLocked(true);

    // Clear session timer
    if (freeSessionTimerRef.current) {
      clearTimeout(freeSessionTimerRef.current);
      freeSessionTimerRef.current = null;
    }

    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Show premium banner IMMEDIATELY (before stopping audio)
    setShowPremiumBanner(true);
    setPremiumCounterMessage('');

    // Fade out voice via GainNode (~3s)
    const ctx = audioCtxRef.current;
    const now = ctx?.currentTime || 0;
    if (voiceGainRef.current && ctx) {
      voiceGainRef.current.gain.cancelScheduledValues(now);
      voiceGainRef.current.gain.setValueAtTime(voiceGainRef.current.gain.value, now);
      voiceGainRef.current.gain.linearRampToValueAtTime(0, now + 3);
    }

    // Fade out music via audio.volume (~3s)
    const bgMusic = currentAudioRef.current?.backgroundMusic;
    if (bgMusic) {
      const startVol = bgMusic.volume;
      const steps = 60;
      const stepSize = startVol / steps;
      let step = 0;
      const fadeInterval = setInterval(() => {
        step++;
        if (step >= steps) {
          bgMusic.volume = 0;
          clearInterval(fadeInterval);
        } else {
          bgMusic.volume = Math.max(0, startVol - stepSize * step);
        }
      }, 50); // 60 steps * 50ms = 3000ms
    }

    // After fade completes, stop everything
    setTimeout(() => {
      if (currentAudioRef.current) {
        currentAudioRef.current.isPlayingSession = false;

        if (currentAudioRef.current.voiceAudio) {
          try { currentAudioRef.current.voiceAudio.onended = null; currentAudioRef.current.voiceAudio.pause(); currentAudioRef.current.voiceAudio.src = ''; URL.revokeObjectURL(currentAudioRef.current.voiceBlobUrl); } catch (e) {}
          currentAudioRef.current.voiceAudio = null;
        }

        if (currentAudioRef.current.backgroundMusic) {
          currentAudioRef.current.backgroundMusic.pause();
          currentAudioRef.current.backgroundMusic.onended = null;
          currentAudioRef.current.backgroundMusic = null;
        }

        if (messageIntervalRef.current) {
          clearInterval(messageIntervalRef.current);
          messageIntervalRef.current = null;
        }
      }
    }, 3100);
  };

  /**
   * Graceful session end: fade out all audio over ~1.5s, then cleanup and navigate
   */
  const gracefulStop = (destination) => {
    const FADE_MS = 1500;
    const ctx = audioCtxRef.current;
    const now = ctx?.currentTime || 0;

    // Fade voice GainNode to 0 (voice uses BufferSourceNode)
    if (voiceGainRef.current && ctx) {
      voiceGainRef.current.gain.cancelScheduledValues(now);
      voiceGainRef.current.gain.setValueAtTime(voiceGainRef.current.gain.value, now);
      voiceGainRef.current.gain.linearRampToValueAtTime(0, now + FADE_MS / 1000);
    }

    // Fade music via audio.volume
    const bgMusic = currentAudioRef.current?.backgroundMusic;
    if (bgMusic) {
      const startVol = bgMusic.volume;
      const steps = Math.round(FADE_MS / 50);
      const stepSize = startVol / steps;
      let step = 0;
      const fadeTimer = setInterval(() => {
        step++;
        if (step >= steps) {
          bgMusic.volume = 0;
          clearInterval(fadeTimer);
        } else {
          bgMusic.volume = Math.max(0, startVol - stepSize * step);
        }
      }, 50);
    }

    // After fade completes, do the hard stop + navigate
    setTimeout(() => {
      stopAudio();
      navigate(destination);
    }, FADE_MS);
  };

  const stopAudio = () => {
    if (currentAudioRef.current) {
      // Mark session as stopped to prevent new tracks from playing
      currentAudioRef.current.isPlayingSession = false;

      // Cancel any ducking animation
      if (duckIntervalRef.current) {
        clearInterval(duckIntervalRef.current);
        duckIntervalRef.current = null;
      }

      // Stop voice (HTML audio element)
      if (currentAudioRef.current.voiceAudio) {
        try {
          currentAudioRef.current.voiceAudio.onended = null;
          currentAudioRef.current.voiceAudio.pause();
          currentAudioRef.current.voiceAudio.src = '';
          URL.revokeObjectURL(currentAudioRef.current.voiceBlobUrl);
        } catch (e) { /* already stopped */ }
        currentAudioRef.current.voiceAudio = null;
      }

      // Stop background music (Audio element)
      if (currentAudioRef.current.backgroundMusic) {
        const bgMusic = currentAudioRef.current.backgroundMusic;
        bgMusic.pause();
        bgMusic.onended = null;
        bgMusic.onerror = null;
        try { bgMusic.removeAttribute('src'); } catch (e) {}
        currentAudioRef.current.backgroundMusic = null;
      }

      // Clear voice timer
      if (currentAudioRef.current.voiceTimer) {
        clearTimeout(currentAudioRef.current.voiceTimer);
        currentAudioRef.current.voiceTimer = null;
      }

      // Clear regular message interval and initial timer
      startRegularCycleRef.current = null;
      if (currentAudioRef.current.initialTimer) {
        clearTimeout(currentAudioRef.current.initialTimer);
        currentAudioRef.current.initialTimer = null;
      }
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }

      // Clear free session timer
      if (freeSessionTimerRef.current) {
        clearTimeout(freeSessionTimerRef.current);
        freeSessionTimerRef.current = null;
      }
    }

    // Release audio session
    audioSessionManager.setPlaying('sos_calm', false, 'session_stop');
    audioSessionManager.releaseSession('sos_calm', true);
    audioSessionManager.unregisterResumeCallback('sos_calm');
    audioSessionManager.unregisterStopCallback('sos_calm');

    setIsPlaying(false);
    setCurrentMessageIndex(-1);
  };

  const handleNextTrack = () => {
    if (!currentAudioRef.current?.isPlayingSession) return;

    const bgMusic = currentAudioRef.current?.backgroundMusic;
    if (!bgMusic) {
      playRandomBackgroundMusic();
      return;
    }

    // Fade out current track over 800ms via audio.volume
    const startVol = bgMusic.volume;
    const steps = 16;
    const stepSize = startVol / steps;
    let step = 0;
    const fadeTimer = setInterval(() => {
      step++;
      if (step >= steps) {
        bgMusic.volume = 0;
        clearInterval(fadeTimer);
      } else {
        bgMusic.volume = Math.max(0, startVol - stepSize * step);
      }
    }, 50);

    // After fade, stop and play next
    setTimeout(() => {
      bgMusic.pause();
      bgMusic.onended = null;
      bgMusic.removeAttribute('src');
      currentAudioRef.current.backgroundMusic = null;
      musicInitialVolumeRef.current = musicVolumeRef.current;
      playRandomBackgroundMusic();
    }, 850);
  };

  if (!isPlaying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-900 via-green-900 to-teal-900 pb-40">
        <div className="max-w-2xl mx-auto px-6 py-8 text-center">
          {/* Compact Header */}
          <div className="mb-8">
            <div className="w-16 h-16 mx-auto bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm mb-3">
              <Heart size={32} className="text-white" fill="white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {t?.locale === 'fr-FR' ? 'Apaisement immédiat' : 'Immediate Calm'}
            </h1>
            <div className="mb-2">
              <ContentBadge type="relaxation" />
            </div>
            <p className="text-white/60 text-sm mb-2">
              {t?.locale === 'fr-FR' ? 'Retrouvez votre calme en quelques minutes' : 'Find your calm in minutes'}
            </p>
            <p className="text-white/50 text-xs italic">
              {t?.locale === 'fr-FR' ? 'Apaisement immédiat — sans réglage ni correction audio.' : 'Immediate relief — no audio adjustment or correction.'}
            </p>
          </div>

          {/* Feature Cards with Icons */}
          <div className="grid grid-cols-3 gap-3 mb-8 px-2">
            {/* Music */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <Volume2 size={24} className="text-teal-300 mx-auto mb-2" />
              <p className="text-white/90 text-xs font-medium">
                {t?.locale === 'fr-FR' ? 'Musique' : 'Music'}
              </p>
              <p className="text-white/60 text-xs mt-1">
                {t?.locale === 'fr-FR' ? '10 ambiances' : '10 ambiances'}
              </p>
            </div>

            {/* Voice Messages */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <Heart size={24} className="text-teal-300 mx-auto mb-2" />
              <p className="text-white/90 text-xs font-medium">
                {t?.locale === 'fr-FR' ? 'Voix' : 'Voice'}
              </p>
              <p className="text-white/60 text-xs mt-1">
                {t?.locale === 'fr-FR' ? 'Apaisante' : 'Soothing'}
              </p>
            </div>

            {/* Duration */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-teal-300 mx-auto mb-2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <p className="text-white/90 text-xs font-medium">
                {t?.locale === 'fr-FR' ? 'Durée' : 'Duration'}
              </p>
              <p className="text-white/60 text-xs mt-1">
                {t?.locale === 'fr-FR' ? 'Illimitée' : 'Unlimited'}
              </p>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 mb-8">
            <p className="text-white/70 text-sm leading-relaxed">
              {t?.locale === 'fr-FR' 
                ? '💡 Installez-vous confortablement • Utilisez des écouteurs • Respirez profondément'
                : '💡 Get comfortable • Use headphones • Breathe deeply'}
            </p>
          </div>

          {/* Start Button - More prominent */}
          <Button
            onClick={startCalming}
            className="w-full max-w-sm mx-auto bg-gradient-to-r from-teal-500 to-green-500 text-white hover:from-teal-600 hover:to-green-600 py-5 rounded-full text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all"
          >
            {t?.locale === 'fr-FR' ? '🎧 Commencer' : '🎧 Start'}
          </Button>
        </div>
      </div>
    );
  }

  // Playing Screen
  return (
    <div
      className="min-h-screen flex items-center justify-center pb-32 pt-16 relative overflow-hidden"
      data-testid="sos-calm-playing"
      style={{
        background: 'radial-gradient(circle at center, #1b5e4a 0%, #0f3b2e 60%, #071e17 100%)',
      }}
    >
      {/* Subtle grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.035,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
        }}
      />

      {/* Breathing Orb Keyframes */}
      <style>{`
        @keyframes sosBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes sosOrbFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes sosTextFade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sos-slider {
          -webkit-appearance: none;
          appearance: none;
          touch-action: none;
          padding: 14px 0;
          box-sizing: content-box;
          height: 6px;
          border-radius: 3px;
          background: rgba(255,255,255,0.15);
          cursor: pointer;
          margin: 0;
        }
        .sos-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 28px; height: 28px;
          border-radius: 50%;
          background: #3ddc97;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(61,220,151,0.4);
        }
        .sos-slider::-moz-range-thumb {
          width: 28px; height: 28px;
          border-radius: 50%;
          background: #3ddc97;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 6px rgba(61,220,151,0.4);
        }
        .sos-btn-active {
          transform: scale(0.93);
          opacity: 0.6;
        }
        .sos-btn {
          transition: transform 0.15s ease, opacity 0.15s ease, background 0.15s ease;
        }
        .sos-btn:active {
          transform: scale(0.93);
          opacity: 0.6;
        }
      `}</style>

      {/* Premium Upsell Banner */}
      {showPremiumBanner && isFreeUser && (
        <div
          className="fixed left-0 right-0 z-50 border-b border-white/20"
          data-testid="premium-upsell-banner"
          style={{
            top: '90px',
            background: 'rgba(96, 48, 255, 0.35)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderRadius: '18px',
            margin: '0 16px',
          }}
        >
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-white text-lg font-bold mb-1">
                  {locale === 'fr-FR' ? 'Votre moment de calme' : 'Your moment of calm'}
                </h3>
                <p className="text-white/90 text-sm">
                  {locale === 'fr-FR'
                    ? 'Votre session gratuite de 5 minutes est terminée. Passez Premium pour un apaisement illimité.'
                    : 'Your 5-minute free session is over. Upgrade to Premium for unlimited calm.'}
                </p>
              </div>
              <button
                onClick={() => navigate('/premium')}
                data-testid="premium-upgrade-btn"
                className="ml-4 px-6 py-3 bg-white text-[#6F42EA] hover:bg-white/90 rounded-full font-semibold shadow-lg transition-all hover:scale-105"
              >
                {locale === 'fr-FR' ? 'Passer à Premium' : 'Upgrade to Premium'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div
        className="relative z-10 flex flex-col items-center px-6 w-full"
        style={{
          maxWidth: '600px',
          opacity: showPremiumBanner ? 0.35 : 1,
          pointerEvents: showPremiumBanner ? 'none' : 'auto',
          filter: showPremiumBanner ? 'blur(1px)' : 'none',
          transition: 'all 0.45s ease',
        }}
      >
        {/* Breathing Orb */}
        <div
          data-testid="breathing-orb"
          style={{
            width: '160px',
            height: '160px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #3ddc97, #1f7a5c)',
            boxShadow: '0 0 80px rgba(61,220,151,0.35), 0 0 160px rgba(61,220,151,0.12)',
            animation: 'sosBreathe 6s ease-in-out infinite, sosOrbFadeIn 1.5s ease-out',
            flexShrink: 0,
          }}
        />

        {/* Voice message text or default */}
        <p
          data-testid="sos-calm-message"
          style={{
            marginTop: '40px',
            fontSize: '24px',
            fontWeight: 300,
            lineHeight: 1.5,
            color: 'white',
            textAlign: 'center',
            maxWidth: '600px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '72px',
            animation: (currentVoiceText && voiceTextVisible) ? 'sosTextFade 0.6s ease-out' : 'none',
            opacity: (currentVoiceText ? voiceTextVisible : showSafeMessage) ? 1 : 0,
            transition: 'opacity 1.2s ease-in-out',
          }}
        >
          {currentVoiceText || (locale === 'fr-FR' ? 'Tu es en sécurité ici' : 'You are safe here')}
        </p>

        {/* Support line */}
        <p
          data-testid="sos-calm-support"
          style={{
            fontSize: '15px',
            opacity: 0.5,
            marginTop: '8px',
            color: 'white',
            fontWeight: 300,
            letterSpacing: '0.02em',
          }}
        >
          {locale === 'fr-FR' ? 'Prends ton temps' : 'Take your time'}
        </p>

        {/* Premium Counter Message - FREE users only */}
        {isFreeUser && premiumCounterMessage && !showPremiumBanner && (
          <div
            data-testid="premium-counter"
            className="backdrop-blur-md rounded-2xl px-5 py-3 border border-white/15"
            style={{ marginTop: '24px', background: 'rgba(111,66,234,0.25)' }}
          >
            <p className="text-white/90 text-sm font-medium">{premiumCounterMessage}</p>
          </div>
        )}

        {/* Buttons */}
        <div style={{ marginTop: '50px' }} className="flex flex-col items-center gap-4 w-full">
          {/* Continue listening - primary (Premium only) */}
          {!isFreeUser && (
            <button
              onClick={() => {
                if (nextTrackCooldown) return;
                setNextTrackCooldown(true);
                handleNextTrack();
                setTimeout(() => setNextTrackCooldown(false), 1200);
              }}
              disabled={isLocked || nextTrackCooldown}
              data-testid="continue-listening-btn"
              className={`sos-btn ${nextTrackCooldown ? 'sos-btn-active' : ''}`}
              style={{
                width: '100%',
                maxWidth: '320px',
                padding: '14px 32px',
                borderRadius: '9999px',
                background: nextTrackCooldown ? 'rgba(61,220,151,0.08)' : 'rgba(61,220,151,0.18)',
                border: '1px solid rgba(61,220,151,0.3)',
                color: 'white',
                fontSize: '15px',
                fontWeight: 500,
                letterSpacing: '0.01em',
                cursor: (isLocked || nextTrackCooldown) ? 'not-allowed' : 'pointer',
                opacity: (isLocked || nextTrackCooldown) ? 0.4 : 1,
              }}
            >
              {nextTrackCooldown
                ? (locale === 'fr-FR' ? 'Chargement…' : 'Loading…')
                : (locale === 'fr-FR' ? 'Musique suivante' : 'Next track')
              }
            </button>
          )}

          {/* End session — visible night-safe */}
          <button
            onClick={() => {
              if (endSessionCooldown) return;
              setEndSessionCooldown(true);
              gracefulStop('/sos');
            }}
            disabled={endSessionCooldown}
            data-testid="end-session-btn"
            className={`min-h-[48px] px-5 py-3 rounded-2xl bg-white/12 border border-white/25 backdrop-blur-md transition-all duration-200 active:scale-95 shadow-lg ${endSessionCooldown ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={t?.endSession || (locale === 'fr-FR' ? 'Fin de session' : 'End session')}
          >
            <span className="flex items-center justify-center gap-2 text-sm text-white font-semibold tracking-wide">
              <span aria-hidden="true" className="text-base">🏡</span>
              <span>
                {endSessionCooldown
                  ? (locale === 'fr-FR' ? 'Fin en cours…' : 'Ending…')
                  : (t?.endSession || (locale === 'fr-FR' ? 'Fin de session' : 'End session'))}
              </span>
            </span>
          </button>
        </div>

        {/* Advanced Breathing - Premium Only */}
        {(user?.isPremium || user?.subscription === 'premium' || user?.role === 'admin') && (
          <div style={{ marginTop: '40px', width: '100%', maxWidth: '440px' }}>
            <div
              onClick={() => gracefulStop('/breathing-premium')}
              data-testid="advanced-breathing-btn"
              className="cursor-pointer transition-all hover:scale-[1.02]"
              style={{
                background: 'rgba(111,66,234,0.2)',
                border: '1px solid rgba(111,66,234,0.3)',
                borderRadius: '16px',
                padding: '16px 20px',
              }}
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <h3 className="text-white/90 text-sm font-medium">
                    {locale === 'fr-FR' ? 'Respiration Avancée' : 'Advanced Breathing'}
                  </h3>
                  <p className="text-white/50 text-xs mt-0.5">
                    {locale === 'fr-FR' ? '8 exercices • Mode premium' : '8 exercises • Premium mode'}
                  </p>
                </div>
                <div className="text-white/30 text-lg">→</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SOSCalm;
